import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Briefcase, MapPin, Clock, Building2, ArrowLeft, Send, FileText, 
  Calendar, DollarSign, Users, CheckCircle, X, ChevronRight
} from 'lucide-react';
import { jobsAPI, clientDocumentsAPI } from '../services/api';
import { toast } from 'sonner';

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [userDocuments, setUserDocuments] = useState([]);
  const [applyForm, setApplyForm] = useState({
    resume_url: '',
    cover_letter: ''
  });
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await jobsAPI.getById(id);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Offre non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = async () => {
    const token = localStorage.getItem('titelli_token');
    if (!token) {
      toast.error('Connectez-vous pour postuler');
      navigate('/auth');
      return;
    }

    try {
      // Fetch all documents (not just CV category)
      const res = await clientDocumentsAPI.list();
      const allDocs = res.data?.documents || res.data || [];
      // Filter to show CV and PDF documents (useful for job applications)
      const relevantDocs = allDocs.filter(d => 
        d.category === 'cv' || d.category === 'general' || d.url?.includes('.pdf')
      );
      setUserDocuments(relevantDocs.length > 0 ? relevantDocs : allDocs);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setUserDocuments([]);
    }
    setShowApplyModal(true);
  };

  const handleSubmitApplication = async () => {
    if (!applyForm.resume_url) {
      toast.error('Veuillez sélectionner un CV');
      return;
    }

    setApplying(true);
    try {
      await jobsAPI.apply(id, {
        resume_url: applyForm.resume_url,
        cover_letter: applyForm.cover_letter
      });
      toast.success('Candidature envoyée avec succès !');
      setShowApplyModal(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4627F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <Briefcase className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Offre non trouvée</h1>
          <p className="text-gray-400 mb-6">Cette offre n'existe pas ou a été supprimée.</p>
          <Link to="/" className="btn-primary">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="job-detail-page">
      {/* Hero Section with Video/Image Background */}
      <div className="relative h-[35vh] min-h-[280px] overflow-hidden">
        <div className="absolute inset-0">
          {job.cover_video ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            >
              <source src={job.cover_video} type="video/mp4" />
            </video>
          ) : (
            <img 
              src={job.cover_image || "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"} 
              alt="" 
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
        </div>
        
        <div className="relative h-full flex items-end">
          <div className="max-w-4xl mx-auto px-4 md:px-8 w-full pb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Retour aux offres
            </Link>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                job.type === 'CDI' ? 'bg-green-500/20 text-green-400' :
                job.type === 'CDD' ? 'bg-orange-500/20 text-orange-400' :
                job.type === 'Stage' ? 'bg-purple-500/20 text-purple-400' :
                job.type === 'Freelance' ? 'bg-cyan-500/20 text-cyan-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {job.type || 'CDI'}
              </span>
              {job.is_urgent && (
                <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                  Urgent
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              {job.title}
            </h1>
            <p className="text-xl text-[#E58AA0]">{job.enterprise_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card-service rounded-xl p-4 text-center">
                <MapPin className="w-5 h-5 text-[#D4627F] mx-auto mb-2" />
                <p className="text-sm text-gray-400">Lieu</p>
                <p className="text-white font-medium">{job.location || 'Lausanne'}</p>
              </div>
              <div className="card-service rounded-xl p-4 text-center">
                <Clock className="w-5 h-5 text-[#D4627F] mx-auto mb-2" />
                <p className="text-sm text-gray-400">Type</p>
                <p className="text-white font-medium">{job.type || 'Porn'}</p>
              </div>

              <div className="card-service rounded-xl p-4 text-center">
                <Calendar className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                <p className="text-sm text-gray-400">Début</p>
                <p className="text-white font-medium">{job.start_date || 'Dès que possible'}</p>
              </div>
            </div>

            {/* Description */}
            <div className="card-service rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Description du poste</h2>
              <div className="prose prose-invert max-w-none">
                <p className="text-gray-300 whitespace-pre-line">{job.description}</p>
              </div>
            </div>

            {/* Requirements */}
            {job.requirements && (
              <div className="card-service rounded-xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">Profil recherché</h2>
                <ul className="space-y-2">
                  {(typeof job.requirements === 'string' ? job.requirements.split('\n') : job.requirements).map((req, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefits */}
            {job.benefits && (
              <div className="card-service rounded-xl p-6">
                <h2 className="text-xl font-bold text-white mb-4">Avantages</h2>
                <ul className="space-y-2">
                  {(typeof job.benefits === 'string' ? job.benefits.split('\n') : job.benefits).map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-[#E58AA0] mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Card */}
            <div className="card-service rounded-xl p-6 sticky top-24">
              <button
                onClick={handleApplyClick}
                className="w-full btn-primary py-4 text-lg flex items-center justify-center gap-2"
                data-testid="job-apply-btn"
              >
                <Send className="w-5 h-5" />
                Postuler maintenant
              </button>
              
              <p className="text-center text-gray-500 text-sm mt-4">
                {job.applications_count ? `${job.applications_count} candidature(s)` : 'Soyez le premier à postuler !'}
              </p>

              {job.deadline && (
                <div className="mt-4 p-3 bg-orange-500/10 rounded-lg text-center">
                  <p className="text-orange-400 text-sm">
                    Date limite : {new Date(job.deadline).toLocaleDateString('fr-CH')}
                  </p>
                </div>
              )}
            </div>

            {/* Company Card */}
            <div className="card-service rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-[#D4627F]/10 flex items-center justify-center">
                  {job.enterprise_logo ? (
                    <img src={job.enterprise_logo} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  ) : (
                    <Building2 className="w-8 h-8 text-[#D4627F]" />
                  )}
                </div>
                <div>
                  <h3 className="text-white font-semibold">{job.enterprise_name}</h3>
                  <p className="text-gray-400 text-sm">{job.enterprise_category || 'Lexata'}</p>
                </div>
              </div>
          
            </div>

            {/* Share */}

          </div>
        </div>
      </div>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="apply-modal">
          <div className="bg-[#0A0A0A] rounded-2xl w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Postuler à cette offre</h2>
                <button onClick={() => setShowApplyModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Job Info */}
              <div className="bg-white/5 rounded-xl p-4">
                <h3 className="text-white font-semibold">{job.title}</h3>
                <p className="text-[#E58AA0] text-sm">{job.enterprise_name}</p>
                <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">
                  <span>{job.type}</span>
                  <span>{job.location || 'Lausanne'}</span>
                </div>
              </div>
              
              {/* CV Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <FileText className="w-4 h-4 inline mr-2" />
                  Sélectionnez une image de vous*
                </label>
                {userDocuments.length > 0 ? (
                  <div className="space-y-2">
                    {userDocuments.map((doc) => (
                      <label 
                        key={doc.id} 
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          applyForm.resume_url === doc.url 
                            ? 'bg-[#D4627F]/20 border border-[#D4627F]' 
                            : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <input
                          type="radio"
                          name="resume"
                          value={doc.url}
                          checked={applyForm.resume_url === doc.url}
                          onChange={(e) => setApplyForm({...applyForm, resume_url: e.target.value})}
                          className="accent-[#D4627F]"
                        />
                        <FileText className="w-5 h-5 text-[#D4627F]" />
                        <div className="flex-1">
                          <span className="text-white text-sm">{doc.name}</span>
                          <span className="text-xs text-gray-500 ml-2">({doc.category})</span>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-white/5 rounded-xl">
                    <FileText className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm mb-3">Vous n'avez pas encore de CV enregistré</p>
                    <Link 
                      to={`/dashboard/client?tab=documents&returnToJob=${id}`}
                      className="text-[#D4627F] hover:underline text-sm"
                      onClick={() => setShowApplyModal(false)}
                    >
                      Ajouter une image dans mon espace
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Cover Letter */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">message de motivation (optionnel)</label>
                <textarea
                  value={applyForm.cover_letter}
                  onChange={(e) => setApplyForm({...applyForm, cover_letter: e.target.value})}
                  placeholder="Présentez-vous brièvement et expliquez votre motivation..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#D4627F] outline-none h-32 resize-none"
                />
              </div>
              
              {/* Submit */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitApplication}
                  disabled={applying || !applyForm.resume_url}
                  className="flex-1 px-4 py-3 rounded-xl bg-[#D4627F] text-white hover:bg-[#D4627F]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {applying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer ma candidature
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;
